import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Building2, Users2, KeyRound, History, Settings, HeartPulse, ShieldCheck, Settings2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { cn } from "@/lib/utils";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const ADMIN_LINKS = [
  { to: "/erp/governance/users", label: "Users", icon: Users2 },
  { to: "/erp/governance/access", label: "Access", icon: KeyRound },
  { to: "/erp/governance/audit-log", label: "Audit Log", icon: History },
  { to: "/erp/governance/org-settings", label: "Organization Settings", icon: Settings },
  { to: "/erp/governance/system-health", label: "Backup & Health", icon: HeartPulse },
  { to: "/erp/governance/integrity-checks", label: "Integrity Checks", icon: ShieldCheck },
];

/** Bottom of the sidebar: company and administration. (Search, bell, user and log out are in the top bar.) */
export function SideUserPanel({ collapsed }) {
  const location = useLocation();
  const { isOrgAdmin } = useAuth();          // every page in this menu is Admin-only on the server
  const { companies, selectedCompanyId, setSelectedCompanyId } = useCompany() || {};
  const [adminOpen, setAdminOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const adminRef = useRef(null);

  useEffect(() => { setAdminOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!adminOpen) return undefined;
    const h = (e) => {
      if (adminRef.current?.contains(e.target) || document.querySelector("[data-admin-portal]")?.contains(e.target)) return;
      setAdminOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [adminOpen]);

  const toggleAdmin = () => {
    const r = adminRef.current.getBoundingClientRect();
    const h = ADMIN_LINKS.length * 42 + 48;
    setPos(collapsed ? { left: r.right + 8, top: Math.max(8, Math.min(r.top, window.innerHeight - h - 16)) } : { left: r.left, top: Math.max(8, r.top - h - 8) });
    setAdminOpen((v) => !v);
  };

  const adminMenu = adminOpen && createPortal(
    <div data-admin-portal style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }} className="min-w-[210px] overflow-hidden rounded-xl border border-[#4A5260] bg-[#2E353F] py-1 shadow-2xl">
      <p className="px-4 pb-1.5 pt-2.5 text-[10px] font-bold uppercase tracking-widest text-[#6B7A8D]">Administration</p>
      {ADMIN_LINKS.map((l) => (
        <NavLink key={l.to} to={l.to} onClick={() => setAdminOpen(false)} data-testid={`admin-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => cn("flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium transition-colors", isActive ? "bg-[#910029] text-white" : "text-[#ECF4F7]/80 hover:bg-[#454D5C] hover:text-[#ECF4F7]")}>
          <l.icon className="h-4 w-4 shrink-0" /> {l.label}
        </NavLink>
      ))}
    </div>,
    document.body
  );

  if (collapsed) {
    return (
      <div className="flex shrink-0 flex-col items-center gap-2 border-t border-[#4A5260] px-2 py-3">
        {adminMenu}
        {isOrgAdmin && (
          <button ref={adminRef} onClick={toggleAdmin} title="Administration" data-testid="admin-toggle-button"
            className={cn("flex h-8 w-8 items-center justify-center rounded-lg transition-colors", adminOpen ? "bg-[#910029] text-white" : "text-[#8B9BB8] hover:bg-[#454D5C] hover:text-[#ECF4F7]")}>
            <Settings2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="shrink-0 space-y-2.5 border-t border-[#4A5260] px-3 py-3">
      {adminMenu}
      {companies && companies.length > 1 && (
        <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
          <SelectTrigger data-testid="company-selector" className="h-8 w-full gap-1.5 border-[#4A5260] bg-[#454D5C] px-2.5 text-xs text-[#ECF4F7] hover:bg-[#4A5260] focus:ring-[#910029]">
            <Building2 className="h-3.5 w-3.5 shrink-0 text-[#8B9BB8]" />
            <span className="truncate"><SelectValue placeholder="Select company" /></span>
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.code}`}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {isOrgAdmin && (
        <button ref={adminRef} onClick={toggleAdmin} data-testid="admin-toggle-button"
          className={cn("flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium transition-all", adminOpen ? "border-[#910029] bg-[#910029] text-white" : "border-[#4A5260] bg-[#454D5C] text-[#ECF4F7]/80 hover:bg-[#4A5260] hover:text-[#ECF4F7]")}>
          <Settings2 className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 truncate text-left">Administration</span>
          <ChevronDown className={cn("h-3 w-3 shrink-0 transition-transform", adminOpen && "rotate-180")} />
        </button>
      )}
    </div>
  );
}
